import { supabase } from './supabase'

export async function getCurrentUserId(): Promise<string> {
  // Use getSession() rather than getUser() — getUser() makes a live HTTP request to
  // /auth/v1/user on every call, which multiplies into many redundant auth requests
  // when multiple service functions run in parallel. getSession() reads the cached
  // session from memory with zero network cost. The JWT is still validated by
  // Supabase's API gateway (PostgREST) on every actual data request, and RLS policies
  // enforce data isolation at the database level.
  // NOTE: Edge Functions must continue to use supabase.auth.getUser(token) for
  // server-side JWT verification — this change is for client-side code only.
  const { data: { session } } = await supabase.auth.getSession()
  if (!session?.user) throw new Error('Not authenticated')
  return session.user.id
}

/**
 * Ownership verification cache with promise deduplication.
 *
 * The problem with a plain value cache: when 17 service functions fire simultaneously
 * inside Promise.all, they ALL check the cache before any of them finish the DB query.
 * Every one finds the cache empty and fires its own request — a race condition.
 *
 * The fix: store the in-flight Promise itself. The first caller starts the request and
 * stores the Promise. Every subsequent parallel caller returns the SAME Promise, so
 * only one DB round-trip is made regardless of how many callers are waiting.
 *
 * Once resolved, the result is stored with a timestamp (TTL 10 seconds) so subsequent
 * calls after the batch completes are served from the resolved value directly.
 *
 * RLS policies at the database level remain the primary security enforcement;
 * this application-layer check is defence-in-depth.
 * Key format: `userId:propertyId`.
 */
interface OwnershipEntry {
  promise: Promise<string>
  resolvedAt?: number
}
const _ownershipCache = new Map<string, OwnershipEntry>()

/**
 * Verify the current user owns the given property.
 * Throws if not authenticated or property not found/not owned.
 * Returns userId for convenience.
 *
 * Parallel calls for the same property share a single in-flight Promise, ensuring
 * exactly one DB query fires per batch regardless of how many callers are waiting.
 * Results are cached for 10 seconds after resolution.
 */
export async function verifyPropertyOwnership(propertyId: string, ownerId?: string): Promise<string> {
  const userId = ownerId ?? await getCurrentUserId()
  const cacheKey = `${userId}:${propertyId}`
  const cached = _ownershipCache.get(cacheKey)

  // Return cached entry if the promise has already resolved and is still fresh
  if (cached?.resolvedAt !== undefined && Date.now() - cached.resolvedAt < 10_000) {
    return cached.promise
  }

  // Return in-flight promise if a request is already pending (deduplicates parallel calls)
  if (cached && cached.resolvedAt === undefined) {
    return cached.promise
  }

  // No entry or expired — start a new request.
  // Use an async IIFE to get a real Promise (not PromiseLike) so .catch() is available.
  const promise: Promise<string> = (async () => {
    const { data, error } = await supabase
      .from('properties')
      .select('id')
      .eq('id', propertyId)
      .eq('owner_id', userId)
      .is('deleted_at', null)
      .maybeSingle()
    if (error) throw error
    if (!data) throw new Error('Property not found')
    // Mark as resolved with timestamp so subsequent calls get the fast path
    const entry = _ownershipCache.get(cacheKey)
    if (entry) entry.resolvedAt = Date.now()
    return userId
  })()

  // Clean up on failure so the next call retries (side-effect only — callers still get rejection)
  promise.catch(() => { _ownershipCache.delete(cacheKey) })

  _ownershipCache.set(cacheKey, { promise })
  return promise
}

/**
 * Property access cache with promise deduplication (owner OR tenant).
 * Used for READ operations where both landlords and tenants need access.
 * Landlords are verified via property ownership; tenants via tenancy membership.
 * Key format: `userId:propertyId`.
 */
const _propertyAccessCache = new Map<string, OwnershipEntry>()

/**
 * Verify the current user has access to a property — as owner, tenant with an
 * active tenancy, or team member with portfolio access to the property.
 * Used for READ-only service functions. Throws if access denied. Returns userId.
 */
export async function verifyPropertyAccess(propertyId: string): Promise<string> {
  const userId = await getCurrentUserId()
  const cacheKey = `${userId}:${propertyId}`
  const cached = _propertyAccessCache.get(cacheKey)

  if (cached?.resolvedAt !== undefined && Date.now() - cached.resolvedAt < 10_000) {
    return cached.promise
  }
  if (cached && cached.resolvedAt === undefined) {
    return cached.promise
  }

  const promise: Promise<string> = (async () => {
    // Fast path: check property ownership (cached separately, resolves instantly for landlords)
    const { data: owned } = await supabase
      .from('properties')
      .select('id')
      .eq('id', propertyId)
      .eq('owner_id', userId)
      .is('deleted_at', null)
      .maybeSingle()

    if (owned) {
      const entry = _propertyAccessCache.get(cacheKey)
      if (entry) entry.resolvedAt = Date.now()
      return userId
    }

    // Slow path: check tenancy membership
    const { data: membership } = await supabase
      .from('tenancy_tenants')
      .select('id, tenancy:tenancies!inner(property_id)')
      .eq('tenant_id', userId)
      .eq('tenancy.property_id', propertyId)
      .is('removed_at', null)
      .limit(1)
      .maybeSingle()

    if (membership) {
      const entry = _propertyAccessCache.get(cacheKey)
      if (entry) entry.resolvedAt = Date.now()
      return userId
    }

    // Team member path: check if the property is in the caller's accessible set
    const { data: teamPropertyIds } = await supabase.rpc('get_team_accessible_property_ids', {
      p_user_id: userId,
    })
    if (Array.isArray(teamPropertyIds) && teamPropertyIds.includes(propertyId)) {
      const entry = _propertyAccessCache.get(cacheKey)
      if (entry) entry.resolvedAt = Date.now()
      return userId
    }

    throw new Error('Property not found')
  })()

  promise.catch(() => { _propertyAccessCache.delete(cacheKey) })
  _propertyAccessCache.set(cacheKey, { promise })
  return promise
}

/**
 * Verify the current user has READ access to a tenancy — as property owner,
 * tenant member, or team member with portfolio access. Returns userId.
 * Used for READ-only service functions. Viewers are included.
 */
export async function verifyTenancyAccess(tenancyId: string): Promise<string> {
  const userId = await getCurrentUserId()

  // Check property ownership via tenancy
  const { data: tenancy } = await supabase
    .from('tenancies')
    .select('property_id')
    .eq('id', tenancyId)
    .is('deleted_at', null)
    .single()

  if (!tenancy) throw new Error('Tenancy not found')

  const { data: owned } = await supabase
    .from('properties')
    .select('id')
    .eq('id', tenancy.property_id)
    .eq('owner_id', userId)
    .is('deleted_at', null)
    .maybeSingle()

  if (owned) return userId

  // Check tenancy membership (tenant path)
  const { data: membership } = await supabase
    .from('tenancy_tenants')
    .select('id')
    .eq('tenancy_id', tenancyId)
    .eq('tenant_id', userId)
    .is('removed_at', null)
    .maybeSingle()

  if (membership) return userId

  // Team member path: check if the property is in the caller's accessible set
  const { data: teamPropertyIds } = await supabase.rpc('get_team_accessible_property_ids', {
    p_user_id: userId,
  })
  if (Array.isArray(teamPropertyIds) && teamPropertyIds.includes(tenancy.property_id)) {
    return userId
  }

  throw new Error('Access denied')
}

/**
 * Verify the current user has WRITE access to a tenancy — as property owner
 * or team member with write access (admin/manager, not viewer).
 * Resolves tenancy → property_id then delegates to verifyPropertyWriteAccess.
 * Used for mutating service functions. Viewers are excluded.
 */
export async function verifyTenancyWriteAccess(tenancyId: string): Promise<string> {
  const { data, error } = await supabase
    .from('tenancies')
    .select('property_id')
    .eq('id', tenancyId)
    .is('deleted_at', null)
    .single()

  if (error || !data) throw new Error('Tenancy not found')
  return verifyPropertyWriteAccess(data.property_id)
}

/**
 * Issue access cache with promise deduplication.
 *
 * When multiple service functions (getIssue, getIssuePhotos, getIssueDocuments,
 * getComments) fire simultaneously inside a Promise.all, they all call
 * verifyIssueAccess before any of them finish. Without caching, each fires its
 * own DB query — a redundant round-trip for every parallel caller.
 *
 * The fix mirrors the verifyPropertyOwnership pattern: store the in-flight
 * Promise itself so all parallel callers share a single DB request. Once
 * resolved, the result is cached for 10 seconds (TTL).
 *
 * RLS policies at the database level remain the primary security enforcement;
 * this application-layer check is defence-in-depth.
 * Key format: `userId:issueId`.
 */
interface IssueAccessEntry {
  promise: Promise<void>
  resolvedAt?: number
}
const _issueAccessCache = new Map<string, IssueAccessEntry>()

/**
 * Verify the current user has access to an issue.
 * Access is granted if the user owns the property, reported the issue, is the
 * assigned contractor, or is a team member with accessible-property scope.
 * Throws if not authenticated or access denied.
 *
 * Parallel calls for the same issue share a single in-flight Promise, ensuring
 * exactly one DB query fires per batch regardless of how many callers are waiting.
 * Results are cached for 10 seconds after resolution.
 */
export async function verifyIssueAccess(issueId: string, userId: string): Promise<void> {
  const cacheKey = `${userId}:${issueId}`
  const cached = _issueAccessCache.get(cacheKey)

  // Return cached entry if the promise has already resolved and is still fresh
  if (cached?.resolvedAt !== undefined && Date.now() - cached.resolvedAt < 10_000) {
    return cached.promise
  }

  // Return in-flight promise if a request is already pending (deduplicates parallel calls)
  if (cached && cached.resolvedAt === undefined) {
    return cached.promise
  }

  // No entry or expired — start a new request.
  // Use an async IIFE to get a real Promise (not PromiseLike) so .catch() is available.
  const promise: Promise<void> = (async () => {
    const { data: issue, error } = await supabase
      .from('issues')
      .select('reported_by, assigned_to, assigned_approved_contractor_id, property_id, property:properties!inner(owner_id)')
      .eq('id', issueId)
      .single()
    if (error || !issue) throw new Error('Issue not found')
    const ownerId = (issue.property as unknown as { owner_id: string }).owner_id

    // Check direct matches first (owner, reporter, assigned contractor)
    if (userId === ownerId || userId === issue.reported_by || userId === issue.assigned_to) {
      const entry = _issueAccessCache.get(cacheKey)
      if (entry) entry.resolvedAt = Date.now()
      return
    }

    // Check approved_contractors path (contractor assigned via invite before account activation)
    if (issue.assigned_approved_contractor_id) {
      const { data: ac } = await supabase
        .from('approved_contractors')
        .select('id')
        .eq('id', issue.assigned_approved_contractor_id)
        .eq('contractor_id', userId)
        .maybeSingle()
      if (ac) {
        const entry = _issueAccessCache.get(cacheKey)
        if (entry) entry.resolvedAt = Date.now()
        return
      }
    }

    // Team member path: issue's property is in the caller's accessible set
    // (admin/manager/viewer — write scoping enforced by RLS + caller gates)
    const { data: teamPropertyIds } = await supabase.rpc('get_team_accessible_property_ids', {
      p_user_id: userId,
    })
    if (Array.isArray(teamPropertyIds) && teamPropertyIds.includes(issue.property_id)) {
      const entry = _issueAccessCache.get(cacheKey)
      if (entry) entry.resolvedAt = Date.now()
      return
    }

    throw new Error('Access denied')
  })()

  // Clean up on failure so the next call retries (side-effect only — callers still get rejection)
  promise.catch(() => { _issueAccessCache.delete(cacheKey) })

  _issueAccessCache.set(cacheKey, { promise })
  return promise
}

/*
 * The `property_agents` "agent path".
 *
 * Three functions below used to try a second query after the ownership check:
 *
 *     .from('property_agents').select('id')
 *       .eq('property_id', propertyId).eq('owner_id', userId)
 *
 * It is gone from all three. Measured live on 2026-11-03, before removing it:
 * `property_agents` holds 7 rows, and in EVERY ONE
 * `property_agents.owner_id = properties.owner_id` — the "agent" is the
 * property's own owner, self-assigned, across 2 users. So the path could
 * never admit anyone the ownership fast path had not already admitted, and
 * removing it withdraws access from nobody.
 *
 * It did do one thing the owner path does not: the owner check filters
 * `deleted_at IS NULL` and this one did not, so for 2 of the 7 rows it was
 * the only thing granting write access to a SOFT-DELETED property. Losing
 * that is a tightening, not a regression.
 *
 * `property_agents` itself is untouched — the table, its rows and its
 * policies all remain. This removes app-layer readers only.
 */

/**
 * Verify the current user is authorized to perform agency-level writes
 * (contractors, prospects, spend approvals, calendar events).
 *
 * Allowed: property owners (no team_members row), admin, manager.
 * Denied: viewer and any future role not explicitly allowlisted.
 * Calls the is_agency_writer() SECURITY DEFINER function in the DB.
 * RLS is the real floor — this is defence-in-depth.
 */
export async function verifyAgencyWriteAccess(): Promise<string> {
  const userId = await getCurrentUserId()
  const { data, error } = await supabase.rpc('is_agency_writer', { p_user_id: userId })
  if (error) throw error
  if (data !== true) throw new Error('Write access denied — viewer role cannot perform this action')
  return userId
}

/**
 * Verify the current user can write to a specific property.
 * Used for property-scoped writes (issues, viewings).
 *
 * Allowed: property owner, admin team member,
 *          manager team member with portfolio assignment to the property.
 * Also allowed: tenants (for issue reporting) — checked separately by callers.
 * Denied: viewer team members.
 * RLS is the real floor — this is defence-in-depth.
 */
export async function verifyPropertyWriteAccess(propertyId: string): Promise<string> {
  const userId = await getCurrentUserId()

  // Fast path: check property ownership
  const { data: owned } = await supabase
    .from('properties')
    .select('id')
    .eq('id', propertyId)
    .eq('owner_id', userId)
    .is('deleted_at', null)
    .maybeSingle()

  if (owned) return userId

  // Team member path: writable property IDs (admin + manager, not viewer)
  const { data: writableIds } = await supabase.rpc('get_team_writable_property_ids', {
    p_user_id: userId,
  })
  if (Array.isArray(writableIds) && writableIds.includes(propertyId)) {
    return userId
  }

  throw new Error('Write access denied — insufficient permissions for this property')
}

/**
 * Verify the current user can write financial data for a specific property.
 * Requires BOTH writable access AND financial access.
 *
 * Allowed: property owner, admin team member (always has both),
 *          manager with portfolio assignment AND financial_access=true.
 * Denied: viewer, manager without financial_access.
 * soft_delete_record is SECURITY DEFINER (bypasses RLS) so this app-layer
 * check is the ONLY authorization gate for financial deletes.
 */
export async function verifyFinancialWriteAccess(propertyId: string): Promise<string> {
  const userId = await getCurrentUserId()

  // Fast path: property owner — always has full access
  const { data: owned } = await supabase
    .from('properties')
    .select('id')
    .eq('id', propertyId)
    .eq('owner_id', userId)
    .is('deleted_at', null)
    .maybeSingle()

  if (owned) return userId

  // Team member path: must be in BOTH writable AND financial sets
  const [{ data: writableIds }, { data: financialIds }] = await Promise.all([
    supabase.rpc('get_team_writable_property_ids', { p_user_id: userId }),
    supabase.rpc('get_team_financial_property_ids', { p_user_id: userId }),
  ])

  if (
    Array.isArray(writableIds) && writableIds.includes(propertyId) &&
    Array.isArray(financialIds) && financialIds.includes(propertyId)
  ) {
    return userId
  }

  throw new Error('Financial write access denied — insufficient permissions')
}

/**
 * Verify the current user can read financial data for a specific property.
 * READ bar: does NOT require writable access (viewers with financial_access qualify).
 *
 * Allowed: property owner, admin team member (always qualifies),
 *          manager/viewer with portfolio assignment AND financial_access=true.
 * Denied: viewer/manager without financial_access, tenants.
 *
 * Tenant exclusion: get_team_financial_property_ids requires an accepted
 * team_members row — structurally impossible for tenants (verified via
 * live forge + static analysis, PR #348 discovery).
 */
export async function verifyFinancialReadAccess(propertyId: string): Promise<string> {
  const userId = await getCurrentUserId()

  // Fast path: property owner — always has full access
  const { data: owned } = await supabase
    .from('properties')
    .select('id')
    .eq('id', propertyId)
    .eq('owner_id', userId)
    .is('deleted_at', null)
    .maybeSingle()

  if (owned) return userId

  // Team member path: financial access only (no writable requirement)
  const { data: financialIds } = await supabase.rpc('get_team_financial_property_ids', {
    p_user_id: userId,
  })

  if (Array.isArray(financialIds) && financialIds.includes(propertyId)) {
    return userId
  }

  throw new Error('Financial read access denied — insufficient permissions')
}

/**
 * Verify the current user can create an issue on a specific property.
 * Extends verifyPropertyWriteAccess with an additional tenant path:
 * tenants with an active tenancy on the property may report issues.
 */
export async function verifyIssueCreateAccess(propertyId: string): Promise<string> {
  const userId = await getCurrentUserId()

  // Try the standard property write access (owner, admin, manager)
  try {
    await verifyPropertyWriteAccess(propertyId)
    return userId
  } catch {
    // Fall through to tenant check
  }

  // Tenant path: active tenancy on this property
  const { data: tenancy } = await supabase
    .from('tenancy_tenants')
    .select('id, tenancy:tenancies!inner(property_id)')
    .eq('tenant_id', userId)
    .eq('tenancy.property_id', propertyId)
    .is('removed_at', null)
    .limit(1)
    .maybeSingle()

  if (tenancy) return userId

  throw new Error('Write access denied — insufficient permissions to create issues on this property')
}
